import React, { useEffect, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Table, Button, Modal, Form, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";
import { useAuth } from "../services/authProvider";

const MANAGE_POSITIONS = [3, 4, 8, 9, 23, 99];

const EVENT_TYPES = [
  { value: "activity", label: "กิจกรรม", color: "primary" },
  { value: "exam", label: "สอบ", color: "danger" },
  { value: "holiday", label: "วันหยุด", color: "success" },
  { value: "meeting", label: "ประชุม", color: "info" },
  { value: "other", label: "อื่นๆ", color: "secondary" },
];
const typeInfo = (t) => EVENT_TYPES.find((e) => e.value === t) || EVENT_TYPES[0];

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("th-TH", { year: "numeric", month: "short", day: "numeric" }) : "");
// แปลงเป็น YYYY-MM-DD ตามเวลาท้องถิ่น — ห้ามใช้ toISOString (UTC) เพราะวันที่จาก DB
// serialize มาเป็นเที่ยงคืนเวลาไทย จะเพี้ยนถอยหลังไป 1 วัน
const toInputDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

export default function Calendar() {
  const { setLoading } = useLoading();
  const { posIds } = useAuth();
  const canManage = (posIds || []).some((p) => MANAGE_POSITIONS.includes(p));

  const [skin, setSkin] = useState(localStorage.getItem("skin-mode") ? "dark" : "");
  const switchSkin = (skin) => {
    const tables = document.getElementsByTagName("table");
    for (const table of tables) {
      const theads = table.querySelectorAll("thead");
      if (skin === "dark") {
        for (const thead of theads) { thead.classList.remove("table-secondary"); thead.classList.add("table-primary"); }
        table.classList.add("table-dark");
      } else {
        table.classList.remove("table-dark");
        for (const thead of theads) { thead.classList.remove("table-primary"); thead.classList.add("table-secondary"); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ cal_title: "", cal_description: "", cal_start_date: "", cal_end_date: "", cal_type: "activity" });

  const loadEvents = () => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getCalendarEvents`)
      .then((res) => setEvents(Array.isArray(res.data) ? res.data : []))
      .catch((err) => { console.error(err); Swal.fire("ผิดพลาด", "ไม่สามารถโหลดปฏิทินได้", "error"); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadEvents(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ cal_title: "", cal_description: "", cal_start_date: "", cal_end_date: "", cal_type: "activity" });
    setShowModal(true);
  };

  const openEdit = (ev) => {
    setEditing(ev);
    setForm({
      cal_title: ev.cal_title, cal_description: ev.cal_description || "",
      cal_start_date: toInputDate(ev.cal_start_date), cal_end_date: toInputDate(ev.cal_end_date), cal_type: ev.cal_type || "activity",
    });
    setShowModal(true);
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!form.cal_title.trim()) return Swal.fire("แจ้งเตือน", "กรุณาระบุชื่อกิจกรรม", "warning");
    if (!form.cal_start_date) return Swal.fire("แจ้งเตือน", "กรุณาระบุวันที่เริ่ม", "warning");

    const payload = { ...form, cal_title: form.cal_title.trim(), cal_end_date: form.cal_end_date || null };
    const req = editing
      ? axiosInstance.put(`${process.env.REACT_APP_API}/updateCalendarEvent/${editing.cal_id}`, payload)
      : axiosInstance.post(`${process.env.REACT_APP_API}/addCalendarEvent`, payload);

    setLoading(true);
    req
      .then(() => { setShowModal(false); Swal.fire("สำเร็จ", editing ? "แก้ไขกิจกรรมเรียบร้อย" : "เพิ่มกิจกรรมเรียบร้อย", "success"); loadEvents(); })
      .catch((err) => { console.error(err); Swal.fire("ผิดพลาด", err.response?.data?.message || "บันทึกไม่สำเร็จ", "error"); })
      .finally(() => setLoading(false));
  };

  const handleDelete = (ev) => {
    Swal.fire({
      title: "ยืนยันการลบ", text: `ลบกิจกรรม "${ev.cal_title}" ?`, icon: "warning",
      showCancelButton: true, confirmButtonText: "ลบ", cancelButtonText: "ยกเลิก", confirmButtonColor: "#dc3545",
    }).then((result) => {
      if (!result.isConfirmed) return;
      setLoading(true);
      axiosInstance.delete(`${process.env.REACT_APP_API}/deleteCalendarEvent/${ev.cal_id}`)
        .then(() => { Swal.fire("ลบแล้ว", "ลบกิจกรรมเรียบร้อย", "success"); loadEvents(); })
        .catch((err) => { console.error(err); Swal.fire("ผิดพลาด", err.response?.data?.message || "ลบไม่สำเร็จ", "error"); })
        .finally(() => setLoading(false));
    });
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ทั่วไป</Link></li>
              <li className="breadcrumb-item active" aria-current="page">ปฏิทินการศึกษา</li>
            </ol>
            <h4 className="main-title mb-0">ปฏิทินการศึกษา</h4>
          </div>
          {canManage && (
            <Button variant="primary" className="d-flex align-items-center gap-2" onClick={openAdd}>
              <i className="ri-add-line fs-18 lh-1"></i> เพิ่มกิจกรรม
            </Button>
          )}
        </div>

        <Row className="g-3">
          <Col xl="12">
            <Card className="card-one">
              <Card.Body className="p-3">
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: "180px" }}>วันที่</th>
                      <th style={{ width: "110px" }} className="text-center">ประเภท</th>
                      <th>กิจกรรม</th>
                      <th style={{ width: "120px" }} className="text-center">จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.length === 0 ? (
                      <tr><td colSpan={4} className="text-center text-secondary py-4">ยังไม่มีกิจกรรมในปฏิทิน</td></tr>
                    ) : (
                      events.map((ev) => {
                        const ti = typeInfo(ev.cal_type);
                        return (
                          <tr key={ev.cal_id}>
                            <td>
                              {formatDate(ev.cal_start_date)}
                              {ev.cal_end_date ? ` - ${formatDate(ev.cal_end_date)}` : ""}
                            </td>
                            <td className="text-center"><Badge bg={ti.color}>{ti.label}</Badge></td>
                            <td>
                              <div className="fw-medium">
                                {ev.cal_title}
                                {ev.cal_no_teaching ? (
                                  // ตั้งค่าจากปฏิทินปฏิบัติงาน (ฝ่ายธุรการ) — หน้านี้แสดงอย่างเดียว
                                  <Badge bg="warning" text="dark" className="ms-2">
                                    งดการเรียนการสอน
                                    {ev.cal_period_start != null && ` คาบ ${ev.cal_period_start}${ev.cal_period_end !== ev.cal_period_start ? `–${ev.cal_period_end}` : ""}`}
                                    {ev.cal_class_scope && ` (${ev.cal_class_scope.split(",").map((c) => `ม.${c}`).join(", ")})`}
                                    {ev.cal_room_scope && ` (เฉพาะ ${ev.cal_room_scope.split(",").length} ห้องเรียน)`}
                                  </Badge>
                                ) : null}
                              </div>
                              {ev.cal_description && <small className="text-secondary">{ev.cal_description}</small>}
                            </td>
                            <td className="text-center">
                              {canManage ? (
                                <div className="d-flex gap-1 justify-content-center">
                                  <Button size="sm" variant="outline-primary" onClick={() => openEdit(ev)}><i className="ri-edit-line"></i></Button>
                                  <Button size="sm" variant="outline-danger" onClick={() => handleDelete(ev)}><i className="ri-delete-bin-line"></i></Button>
                                </div>
                              ) : "-"}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title as="h6">{editing ? "แก้ไขกิจกรรม" : "เพิ่มกิจกรรม"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>ชื่อกิจกรรม <span className="text-danger">*</span></Form.Label>
              <Form.Control type="text" value={form.cal_title} onChange={(e) => setForm({ ...form, cal_title: e.target.value })} />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>วันที่เริ่ม <span className="text-danger">*</span></Form.Label>
                  <Form.Control type="date" value={form.cal_start_date} onChange={(e) => setForm({ ...form, cal_start_date: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>วันที่สิ้นสุด</Form.Label>
                  <Form.Control type="date" value={form.cal_end_date} onChange={(e) => setForm({ ...form, cal_end_date: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>ประเภท</Form.Label>
              <Form.Select value={form.cal_type} onChange={(e) => setForm({ ...form, cal_type: e.target.value })}>
                {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-1">
              <Form.Label>รายละเอียด</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.cal_description} onChange={(e) => setForm({ ...form, cal_description: e.target.value })} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" type="submit">บันทึก</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </React.Fragment>
  );
}
